import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { API, axios } from "@/api";

const VendorModeContext = createContext(null);
export const useVendorMode = () => useContext(VendorModeContext);

const VENDOR_MODE_KEY = "vendorModeActive";
const MODE_SELECTED_KEY = "vendorModeSelected";

export function VendorModeProvider({ children }) {
  const { user } = useAuth();
  const [vendorMode, setVendorMode] = useState(false);
  const [modeSelected, setModeSelected] = useState(false);
  const [loading, setLoading] = useState(true);

  const isVendor = user?.role === "vendor";
  const isVerified = isVendor && user?.vendor_verified === true;
  const isPending = isVendor && !user?.vendor_verified && user?.vendor_verification_status === "pending";

  // Initialize mode on user change
  useEffect(() => {
    if (!user) { setLoading(false); return; }

    const wasSelected = localStorage.getItem(MODE_SELECTED_KEY) === "true";
    const savedMode = localStorage.getItem(VENDOR_MODE_KEY) === "true";

    if (!isVerified) {
      setVendorMode(false);
      setModeSelected(true);
      setLoading(false);
      return;
    }

    // Verified vendor
    const pref = user.vendor_mode_default || "always_vendor";
    if (!wasSelected) {
      // First launch after verification — show mode selector
      setModeSelected(false);
      setLoading(false);
      return;
    }

    // Subsequent launch — apply preference
    if (pref === "always_vendor") setVendorMode(true);
    else if (pref === "always_user") setVendorMode(false);
    else setVendorMode(savedMode); // last_used

    setModeSelected(true);
    setLoading(false);
  }, [user, isVerified]);

  const selectMode = useCallback((isVendorMode) => {
    setVendorMode(isVendorMode);
    setModeSelected(true);
    localStorage.setItem(VENDOR_MODE_KEY, String(isVendorMode));
    localStorage.setItem(MODE_SELECTED_KEY, "true");
  }, []);

  const switchMode = useCallback(() => {
    setVendorMode(prev => {
      const next = !prev;
      localStorage.setItem(VENDOR_MODE_KEY, String(next));
      return next;
    });
  }, []);

  const updateModeDefault = useCallback(async (pref) => {
    try {
      await axios.patch(`${API}/auth/me`, { vendor_mode_default: pref });
    } catch { /* ignore */ }
  }, []);

  return (
    <VendorModeContext.Provider value={{
      vendorMode, isVendor, isVerified, isPending,
      modeSelected, selectMode, switchMode,
      updateModeDefault, loading,
    }}>
      {children}
    </VendorModeContext.Provider>
  );
}
